import React from 'react';
import { Shield } from 'lucide-react';
import PublicHeader from '../components/common/PublicHeader';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader />
      <div className="py-12 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow p-8">
        {/* Header */}
        <div className="mb-8 flex items-start gap-3">
          <Shield className="h-8 w-8 text-yellow-600 flex-shrink-0 mt-1" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
            <p className="text-gray-600 mt-1">Last Updated: {new Date().toLocaleDateString()}</p>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-sm max-w-none space-y-6">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">1. Introduction</h2>
            <p className="text-gray-700">
              JustPractice ("we," "us," "our," or "Company") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our legal case management platform and related services (collectively, the "Service").
            </p>
            <p className="text-gray-700 mt-3">
              When you use our Service, JustPractice acts as a <strong>data processor</strong> on your behalf. As a law firm or legal professional, you are the <strong>data controller</strong> responsible for determining how and why client data is processed. We process your data solely to provide the Service and in accordance with your instructions and this Privacy Policy.
            </p>
            <p className="text-gray-700 mt-3">
              Please read this Privacy Policy carefully. If you do not agree with our policies and practices, please do not use our Service.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">2. Information We Collect</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">2.1 Information You Provide Directly</h3>
            <p className="text-gray-700">
              When you create an account or use our Service, we collect information you voluntarily provide, including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
              <li>Name, email address, phone number, and physical address</li>
              <li>Company or organization name</li>
              <li>Case-related information and client details</li>
              <li>Communication preferences, including SMS opt-in status</li>
              <li>Payment and billing information</li>
              <li>Any other information you choose to provide</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">2.2 Information Collected Automatically</h3>
            <p className="text-gray-700">
              We automatically collect certain information about your device and how you interact with our Service:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
              <li>Device information (browser type, IP address, operating system)</li>
              <li>Usage data (pages visited, time spent, actions taken)</li>
              <li>Cookies and similar tracking technologies</li>
              <li>Location information (if you grant permission)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">2.3 SMS Communication</h3>
            <p className="text-gray-700">
              If you opt-in to SMS communications through our intake form, we collect and store your mobile phone number. You explicitly consent to receive SMS messages about appointment reminders, case status updates, and important notices from JustPractice.
            </p>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">3. How We Use Your Information</h2>
            <p className="text-gray-700">
              We use the information we collect for various purposes:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
              <li>Providing, maintaining, and improving our Service</li>
              <li>Creating and managing your account</li>
              <li>Processing transactions and sending related information</li>
              <li>Sending SMS messages (only if you have opted in)</li>
              <li>Communicating with you about your account or Service</li>
              <li>Responding to your inquiries and requests</li>
              <li>Monitoring and analyzing Service usage and trends</li>
              <li>Detecting and preventing fraud or illegal activity</li>
              <li>Complying with legal obligations</li>
              <li>Marketing and promotional communications (only with your consent)</li>
            </ul>
          </section>

          {/* Mobile Information & SMS */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">4. Mobile Information & SMS Communications</h2>
            <p className="text-gray-700">
              <strong>Important:</strong> Your mobile phone number will NOT be shared with third parties for marketing purposes. We use your phone number exclusively for:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
              <li>Sending appointment reminders</li>
              <li>Providing case status updates</li>
              <li>Sending important legal notices or alerts</li>
            </ul>
            <p className="text-gray-700 mt-3">
              <strong>Message Frequency:</strong> Message frequency varies significantly based on your case activity and status. During quiet periods, you may receive minimal messages. During active case phases (discovery, hearing preparation, settlement negotiations, etc.), you may receive multiple messages per day. Message frequency is entirely dependent on the activity and urgency of your legal matter and can range from zero to dozens per week during intensive periods.
            </p>
            <p className="text-gray-700 mt-3">
              <strong>Message and Data Rates:</strong> Message and data rates may apply depending on your mobile carrier. Standard SMS rates from your carrier will apply.
            </p>
            <p className="text-gray-700 mt-3">
              <strong>Opt-Out:</strong> You can opt out of SMS communications at any time by replying STOP to any message. You will receive a confirmation message, and no further SMS messages will be sent to your number.
            </p>
            <p className="text-gray-700 mt-3">
              <strong>Help:</strong> For questions or technical support, reply HELP to any SMS message or contact us at support@justpractice.com.
            </p>
          </section>

          {/* Disclosure of Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">5. Disclosure of Your Information</h2>
            <p className="text-gray-700">
              We may share your information in the following circumstances:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
              <li><strong>Service Providers:</strong> With vendors and contractors who assist us in operating the Service (e.g., hosting providers, SMS service providers like Twilio)</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or government request</li>
              <li><strong>Protection of Rights:</strong> To enforce our agreements and protect the rights, privacy, safety, or property of JustPractice, users, or the public</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets (with notice to you)</li>
            </ul>
            <p className="text-gray-700 mt-3">
              <strong>We do not sell or rent your personal information to third parties for marketing purposes.</strong>
            </p>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">6. Data Security</h2>
            <p className="text-gray-700">
              We implement comprehensive security measures to protect your information. Our infrastructure is hosted on Amazon Web Services (AWS) in the United States, and we employ the following safeguards:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
              <li><strong>Encryption in Transit:</strong> All data transmitted between your browser and our servers is encrypted using TLS 1.2 and TLS 1.3 with AES-256-GCM cipher suites and forward secrecy</li>
              <li><strong>Document Storage Encryption:</strong> Documents uploaded to the Service are stored with AES-256 server-side encryption</li>
              <li><strong>Secure Authentication:</strong> We use secure authentication mechanisms and access controls to protect your account</li>
              <li><strong>IP Address Anonymization:</strong> Login activity records store only anonymized IP addresses to protect your privacy while maintaining security monitoring capabilities</li>
              <li><strong>Employee Confidentiality:</strong> All JustPractice employees and contractors with access to customer data are bound by written confidentiality agreements</li>
              <li><strong>Regular Assessments:</strong> We conduct regular security assessments and updates to maintain the integrity of our systems</li>
            </ul>
            <p className="text-gray-700 mt-3">
              However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          {/* AI and Data Usage */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">7. Artificial Intelligence and Data Usage</h2>
            <p className="text-gray-700">
              JustPractice does not use customer data or client data to train artificial intelligence or machine learning models. When AI-powered features are available within the Service:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
              <li>Your data is processed only to provide the requested functionality and is not retained by AI providers for model training</li>
              <li>Third-party AI providers used by the Service are contractually prohibited from using your data for model training purposes</li>
              <li>AI features operate under the same data security and confidentiality protections described in this Privacy Policy</li>
            </ul>
          </section>

          {/* Google Workspace API Data */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">8. Google Workspace API Usage</h2>
            <p className="text-gray-700">
              JustPractice integrates with Google Workspace APIs, including Google Calendar, to provide calendar synchronization features. When you connect your Google account:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
              <li>We access your Google Calendar data only to sync events between Google Calendar and JustPractice</li>
              <li>Calendar data retrieved from Google is stored securely in our database solely to provide the synchronization functionality you requested</li>
              <li>We do not use your Google data for advertising, marketing, or any purpose unrelated to providing the Service</li>
              <li>You can disconnect your Google account at any time through your JustPractice settings, which will stop all data access</li>
            </ul>
            <p className="text-gray-700 mt-3">
              <strong>Limited Use Disclosure:</strong> JustPractice's use and transfer to any other app of information received from Google APIs will adhere to the{' '}
              <a href="https://developers.google.com/terms/api-services-user-data-policy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Google API Services User Data Policy</a>, including the Limited Use requirements.
            </p>
          </section>

          {/* Your Rights & Choices */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">9. Your Rights and Choices</h2>
            <p className="text-gray-700">
              Depending on your location, you may have certain rights regarding your personal information:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
              <li><strong>Access:</strong> Request access to your personal information</li>
              <li><strong>Correction:</strong> Request correction of inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your information (subject to legal requirements)</li>
              <li><strong>Opt-Out:</strong> Opt out of marketing communications and SMS messages</li>
              <li><strong>Data Portability:</strong> Request a copy of your information in a portable format</li>
            </ul>
            <p className="text-gray-700 mt-3">
              To exercise these rights, contact us at support@justpractice.com.
            </p>
          </section>

          {/* Retention */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">10. Data Retention</h2>
            <p className="text-gray-700">
              We retain your personal information for as long as necessary to provide our Service and fulfill the purposes outlined in this Privacy Policy. Retention periods vary based on the type of information and purpose:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
              <li>Account information: For the duration of your account and for legal/tax purposes thereafter</li>
              <li>SMS consent records: For the duration of your opt-in status plus required legal retention periods</li>
              <li>Transaction records: As required by law (typically 7 years for legal compliance)</li>
              <li>Usage logs: Typically 12 months unless required longer by law</li>
            </ul>
            <p className="text-gray-700 mt-3">
              Upon account deletion, we will retain information only as required by law.
            </p>
          </section>

          {/* Third-Party Links */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">11. Third-Party Links</h2>
            <p className="text-gray-700">
              Our Service may contain links to third-party websites and services. This Privacy Policy does not apply to third-party sites, and we are not responsible for their privacy practices. We encourage you to review the privacy policies of any third-party services before providing your information.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">12. Children's Privacy</h2>
            <p className="text-gray-700">
              Our Service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child, we will take steps to delete such information promptly.
            </p>
          </section>

          {/* Contact Us */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">13. Contact Us</h2>
            <p className="text-gray-700">
              If you have questions about this Privacy Policy or our privacy practices, please contact us:
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-3">
              <p className="text-gray-900 font-semibold">JustPractice Support</p>
              <p className="text-gray-700">Email: support@justpractice.com</p>
            </div>
          </section>

          {/* Policy Changes */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">14. Changes to This Privacy Policy</h2>
            <p className="text-gray-700">
              We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the updated policy on this page and updating the "Last Updated" date. Your continued use of the Service after changes constitutes your acceptance of the updated Privacy Policy.
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            This Privacy Policy is designed to comply with applicable United States federal and state data protection laws and regulations.
          </p>
        </div>
      </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
